// Realtime call WebSocket client - MATCHES experiments/call.py EXACTLY

const API_BASE_URL =
	process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1'

// Enhanced audio queue with gain, low-pass filter, prebuffer, and timestamp scheduling
class AudioPlaybackQueue {
	constructor(audioContext) {
		this.audioContext = audioContext
		this.queue = [] // Simple FIFO queue
		this.maxQueueSize = 200 // Match backend AUDIO_BUFFER_MAXSIZE
		this.isPlaying = false
		this.isActive = true
		this.frameByteSize = 1920 // 40ms @ 24kHz = 1920 bytes
		this.pendingBytes = [] // Coalesce buffer
		this.onPlaybackStart = () => { }
		this.onPlaybackEnd = () => { }
		this.playbackTime = null // WebAudio scheduling clock
		this.frameDurationSec = 0.040 // 40ms per frame
		this.expectedFrameIndex = 0 // Track frame sequence

		// Audio processing nodes - SIMPLIFIED to match working call.py
		// call.py uses raw PCM with NO filtering and works perfectly
		this.masterGain = this.audioContext.createGain()
		this.masterGain.gain.value = 1.0 // Unity gain, no boost
		this.masterGain.connect(this.audioContext.destination)
		// NOTE: Lowpass filter REMOVED - it was causing audio artifacts

		// Prebuffer config (wait for 3 frames before starting)
		this.minStartFrames = 3 // ~120ms prebuffer
		this.started = false

		// Start playback worker
		this.playbackWorker()
	}

	enqueue(audioData) {
		// Drop oldest if full (simple FIFO with max size)
		if (this.queue.length >= this.maxQueueSize) {
			this.queue.shift() // Drop oldest
			console.warn('Audio queue full, dropping oldest frame')
		}
		this.queue.push(audioData)
	}

	async playbackWorker() {
		while (this.isActive) {
			// Wait for queue to have data
			if (this.queue.length === 0) {
				await this.sleep(50)
				if (this.isPlaying) {
					this.isPlaying = false
					this.onPlaybackEnd()
				}
				continue
			}

			// Prebuffer: wait for 3 frames before starting playback
			if (!this.started && this.queue.length < this.minStartFrames) {
				await this.sleep(10)
				continue
			}
			this.started = true

			const chunk = this.queue.shift()

			// Frame size guard (prevent corrupted frames from desyncing playback)
			if (!(chunk.data instanceof ArrayBuffer) || chunk.data.byteLength !== this.frameByteSize) {
				// Allow short final frames at generation end; otherwise skip
				if (chunk.data.byteLength && chunk.data.byteLength < this.frameByteSize) {
					// Pad with zeros to exact frame size
					const padded = new Uint8Array(this.frameByteSize)
					padded.set(new Uint8Array(chunk.data), 0)
					chunk.data = padded.buffer
				} else {
					console.warn('Bad frame size:', chunk.data.byteLength, 'expected:', this.frameByteSize)
					continue
				}
			}

			if (!this.isPlaying) {
				this.isPlaying = true
				this.onPlaybackStart()
			}

			// Play frame directly from ArrayBuffer (no Base64 decoding!)
			await this.playFrame(chunk.data, chunk.rate)
		}
	}

	async playFrame(frameArrayBuffer, sampleRate) {
		try {
			if (this.audioContext.state === 'suspended') {
				await this.audioContext.resume()
			}

			// Convert ArrayBuffer directly to Int16Array (no Base64!)
			const int16Array = new Int16Array(frameArrayBuffer)
			const float32Array = new Float32Array(int16Array.length)
			for (let i = 0; i < int16Array.length; i++) {
				float32Array[i] = int16Array[i] / 32768.0
			}

			// Create AudioBuffer
			const rate = sampleRate || 24000
			const audioBuffer = this.audioContext.createBuffer(
				1, // Mono
				float32Array.length,
				rate // 24kHz from backend
			)
			audioBuffer.copyToChannel(float32Array, 0)

			const source = this.audioContext.createBufferSource()
			source.buffer = audioBuffer

			// Route directly to gain -> destination (NO filter, matches call.py)
			source.connect(this.masterGain)

			// Timestamp-based scheduling
			if (this.playbackTime === null) {
				this.playbackTime = this.audioContext.currentTime + 0.05
			}

			// If network hiccup put us in the past, snap slightly ahead of 'now'
			const now = this.audioContext.currentTime
			if (this.playbackTime < now) {
				this.playbackTime = now + 0.01
			}

			source.start(this.playbackTime)

			// Advance clock by frame duration
			this.playbackTime += this.frameDurationSec

			// Keep schedule moving
			return
		} catch (error) {
			console.error('Audio playback error:', error)
		}
	}

	sleep(ms) {
		return new Promise((resolve) => setTimeout(resolve, ms))
	}

	clear() {
		this.queue = []
		this.pendingBytes = []
		this.isPlaying = false
		this.started = false
		this.playbackTime = null
	}

	destroy() {
		this.isActive = false
		this.clear()
		try { this.masterGain.disconnect() } catch (_) { }
		try { this.lowpass.disconnect() } catch (_) { }
	}
}

export class RealtimeCallClient {
	constructor({ token, replyStyle = 'long', conversationId = null, debug = false }) {
		this.token = token
		this.replyStyle = replyStyle
		this.conversationId = conversationId
		this.debug = !!debug
		this.ws = null
		this.audioContext = null
		this.playbackNode = null
		this.micStream = null
		this.mediaRecorder = null
		this.screenStream = null
		this.onTranscript = () => { }
		this.onInfo = () => { }
		this.onDebug = () => { }
		this.onError = () => { }
		this.onState = () => { }
		this.onConversationSaved = () => { }
		this.onMessageSaved = () => { }
		this.onAudioLevel = () => { }
		this._connected = false
		this._closed = false
		this.analyser = null
		this.analyserDataArray = null
		this.audioQueue = null // Simple FIFO playback queue
		this.frameDuration = 40 // Backend uses 40ms frames (NOT 20ms!)

		// Playback gating (TTS) - matches backend
		this.isSpeaking = false // true while TTS playback in progress
		this.playbackGuardUntil = 0

		// Outbound mic/audio
		this.micQueue = []
		this.audioSendCount = 0

		// Stream management - matches backend
		this._lastMicSendTs = 0
		this._lastTurnCompleteTs = 0

		// Audio format constants - EXACT backend match
		this.INPUT_RATE = 48000 // Mic captures at 48kHz
		this.API_RATE = 16000 // Send to API at 16kHz (downsample)
		this.OUTPUT_RATE = 24000 // Model returns 24kHz PCM
		this.CHUNK_SIZE = 1024 // Mic read chunk
		this.BUFFER_SIZE = 4096 // Playback buffer size

		// Frame configuration - MATCHES backend 40ms frames
		this.FRAME_MS = 40 // 40ms frames (NOT 20ms!)
		this.FRAME_BYTES = 1920 // 24000 * 0.04 * 2 = 1920 bytes

		// Timing - matches backend
		this.MIC_GUARD_MS = 200 // Block mic for 200ms after playback
		this.SILENCE_FLUSH_MS = 1200 // Flush after 1.2s of silence

		// Barge-in detection (simple RMS-based)
		this._bargeThreshold = 0.05 // speech RMS threshold
		this._bargeMinFrames = 3 // consecutive frames to trigger
		this._bargeSpeechFrames = 0

		// Track current TTS source for interruption
		this._ttsSrc = null
		this._silenceEndSentAt = 0
	}

	get connected() {
		return this._connected
	}

	async connect() {
		if (this.ws) return

		// Build URL with URLSearchParams for clean parameter handling
		const params = new URLSearchParams({
			reply_style: this.replyStyle,
			...(this.conversationId ? { conversation_id: this.conversationId } : {}),
			...(this.debug ? { debug: '1' } : {}),
			...(this.token ? { token: this.token } : {}),
		})
		const url = `${API_BASE_URL.replace(/^http/, 'ws')}/realtime/call/live?${params}`

		return new Promise((resolve, reject) => {
			const ws = new WebSocket(url)
			ws.binaryType = 'arraybuffer' // CRITICAL: Handle binary audio frames
			ws.onopen = () => {
				this._connected = true
				this.ws = ws
				this._send({ type: 'hello' })
				this.onState('connected')

				// Initialize simple FIFO audio queue (matches backend)
				if (!this.audioContext) {
					this.audioContext = new (window.AudioContext ||
						window.webkitAudioContext)({
							latencyHint: 'interactive',
							sampleRate: 48000, // Standard rate for WebAudio
						})
				}

				// Destroy old audio queue if exists (prevents overlapping audio)
				if (this.audioQueue) {
					this.audioQueue.destroy()
					this.audioQueue = null
				}

				// Create simple playback queue (NO adaptive jitter buffer)
				this.audioQueue = new AudioPlaybackQueue(this.audioContext)
				this.audioQueue.onPlaybackStart = () => {
					this.isSpeaking = true
				}
				this.audioQueue.onPlaybackEnd = () => {
					// Guard mic for 200ms after playback ends
					this.playbackGuardUntil = Date.now() + this.MIC_GUARD_MS
					this.isSpeaking = false
				}

				// AudioContext watchdog (keeps audio alive across tab switches)
				this._audioWatchdog = setInterval(() => {
					try {
						if (this.audioContext && this.audioContext.state === 'suspended') {
							this.audioContext.resume()
						}
					} catch (_) { }
				}, 2000)

				resolve()
			}
			ws.onerror = (e) => {
				this.onError(`WebSocket error: ${e.message || 'Connection failed'}`)
				reject(e)
			}
			ws.onclose = () => {
				this._connected = false
				this._closed = true
				this._teardownAudio()
				this.onState('closed')
			}
			ws.onmessage = (evt) => {
				try {
					// Handle binary audio frames (ArrayBuffer)
					if (evt.data instanceof ArrayBuffer) {
						if (this.audioQueue) {
							this.audioQueue.enqueue({
								data: evt.data, // Raw ArrayBuffer (24kHz PCM16, 40ms = 1920 bytes)
								rate: this.OUTPUT_RATE, // 24000 Hz
							})
						}
						return
					}

					// Handle text JSON messages
					const msg = JSON.parse(evt.data)

					if (msg.type === 'transcript') {
						this.onTranscript(msg.text || '')
					} else if (msg.type === 'info') {
						this.onInfo(msg.message || '')
					} else if (msg.type === 'debug') {
						this.onDebug(msg)
					} else if (msg.type === 'error') {
						this.onError(msg.message || 'Unknown error')
					} else if (msg.type === 'conversation_saved') {
						// Handle conversation saved event from backend
						this.onConversationSaved(msg.conversation_id || null)
					} else if (msg.type === 'message_saved') {
						// Handle message saved event from backend
						this.onMessageSaved({
							role: msg.role,
							content: msg.content,
							timestamp: msg.timestamp,
							conversationId: msg.conversation_id,
						})
					}
				} catch (e) {
					console.error('Error parsing WebSocket message:', e)
				}
			}
		})
	}

	_send(obj) {
		if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return false
		this.ws.send(JSON.stringify(obj))
		return true
	}

	enableDebug() {
		this.debug = true
		if (this.ws && this.ws.readyState === WebSocket.OPEN) {
			this._send({ type: 'debug_on' })
		}
	}

	disableDebug() {
		this.debug = false
		if (this.ws && this.ws.readyState === WebSocket.OPEN) {
			this._send({ type: 'debug_off' })
		}
	}

	requestStats() {
		this._send({ type: 'request_stats' })
	}

	playDiagnosticTone(durationMs = 1500, frequency = 440, amplitude = 0.3) {
		this._send({ type: 'diagnostic_tone', params: { duration_ms: durationMs, frequency, amplitude } })
	}

	// For manual testing
	sendTestResponse() {
		return this._send({ type: 'test_response' })
	}

	async startMic() {
		try {
			if (!this.audioContext) {
				this.audioContext = new (window.AudioContext ||
					window.webkitAudioContext)({
						latencyHint: 'interactive',
						sampleRate: this.INPUT_RATE,
					})
			}
			this.micStream = await navigator.mediaDevices.getUserMedia({
				audio: {
					channelCount: 1,
					sampleRate: 48000, // Capture at 48kHz for best quality
					echoCancellation: true,
					noiseSuppression: true,
					autoGainControl: true,
					latency: 0.01, // 10ms latency target
				},
				video: false,
			})
		} catch (error) {
			let message = 'Errore microfono sconosciuto'
			if (
				error.name === 'NotAllowedError' ||
				error.name === 'PermissionDeniedError'
			)
				message = 'Permesso microfono negato.'
			else if (
				error.name === 'NotFoundError' ||
				error.name === 'DevicesNotFoundError'
			)
				message = 'Nessun microfono trovato.'
			else if (
				error.name === 'NotReadableError' ||
				error.name === 'TrackStartError'
			)
				message = "Microfono occupato da un'altra app."
			else if (
				error.name === 'OverconstrainedError' ||
				error.name === 'ConstraintNotSatisfiedError'
			)
				message = 'Impostazioni microfono non supportate.'
			else if (error.message) message = error.message
			this.onError(message)
			throw error
		}

		const source = this.audioContext.createMediaStreamSource(this.micStream)
		const processor = this.audioContext.createScriptProcessor(2048, 1, 1)

		// Setup audio analyzer for visualization
		this.analyser = this.audioContext.createAnalyser()
		this.analyser.fftSize = 256
		this.analyser.smoothingTimeConstant = 0.8
		const bufferLength = this.analyser.frequencyBinCount
		this.analyserDataArray = new Uint8Array(bufferLength)
		source.connect(this.analyser)

		// Start audio level monitoring
		this._startAudioLevelMonitoring()

		// Accumulator for exactly-sized chunks
		let downsampledBuffer = new Float32Array(0)

		const downsampleBuffer = (buffer, srcRate, dstRate) => {
			if (srcRate === dstRate) return buffer
			const ratio = srcRate / dstRate
			const newLength = Math.round(buffer.length / ratio)
			const result = new Float32Array(newLength)
			for (let i = 0; i < newLength; i++) {
				const srcIndex = i * ratio
				const i0 = Math.floor(srcIndex)
				const i1 = Math.min(i0 + 1, buffer.length - 1)
				const frac = srcIndex - i0
				result[i] = buffer[i0] * (1 - frac) + buffer[i1] * frac
			}
			return result
		}

		const floatToPCM16 = (input) => {
			const int16 = new Int16Array(input.length)
			for (let i = 0; i < input.length; i++) {
				let s = Math.max(-1, Math.min(1, input[i]))
				int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff
			}
			return int16
		}

		processor.onaudioprocess = (e) => {
			const now = Date.now()
			const input = e.inputBuffer.getChannelData(0)

			// Compute RMS for VAD / barge-in
			let rms = 0
			for (let i = 0; i < input.length; i++) rms += input[i] * input[i]
			rms = Math.sqrt(rms / Math.max(1, input.length))

			// If TTS is playing, allow barge-in: detect user speech and interrupt
			if (this.isSpeaking) {
				if (rms > this._bargeThreshold) {
					this._bargeSpeechFrames++
					if (this._bargeSpeechFrames >= this._bargeMinFrames) {
						this._bargeSpeechFrames = 0
						this._triggerBargeIn()
					}
				} else {
					this._bargeSpeechFrames = 0
				}
				// Do not stream mic audio while TTS unless barge-in triggers
				return
			}

			// Respect short playback guard right after TTS to avoid self-echo
			if (now < this.playbackGuardUntil) {
				return
			}

			// Downsample to API rate - protect against null audioContext
			if (!this.audioContext) return

			// Apply lightweight 1-pole low-pass ~8kHz at 48kHz to tame aliasing before decimation
			const smoothed = new Float32Array(input.length)
			let z = 0
			const a = 0.22 // alpha ~ 8-10kHz corner at 48kHz
			for (let i = 0; i < input.length; i++) {
				z = z + a * (input[i] - z)
				smoothed[i] = z
			}

			const ds = downsampleBuffer(
				smoothed,
				this.audioContext.sampleRate || this.INPUT_RATE,
				this.API_RATE
			)

			// Append to accumulator
			const merged = new Float32Array(downsampledBuffer.length + ds.length)
			merged.set(downsampledBuffer)
			merged.set(ds, downsampledBuffer.length)
			downsampledBuffer = merged

			// Emit fixed-size chunks continuously
			while (downsampledBuffer.length >= this.CHUNK_SIZE) {
				const chunk = downsampledBuffer.slice(0, this.CHUNK_SIZE)
				downsampledBuffer = downsampledBuffer.slice(this.CHUNK_SIZE)
				const pcm16 = floatToPCM16(chunk)

				// Send raw binary audio (NO Base64!)
				if (this.ws && this.ws.readyState === WebSocket.OPEN) {
					this.ws.send(pcm16.buffer)
					this._lastMicSendTs = now
					this.audioSendCount++
					// if (this.audioSendCount % 10 === 1) {
					// 	console.log(
					// 		`🎤 Sent binary audio chunk #${this.audioSendCount}, size: ${pcm16.buffer.byteLength} bytes`
					// 	)
					// }
				}
			}

			// Auto-flush after 1.2s of silence (matches backend)
			if (this._lastMicSendTs > 0 && now - this._lastMicSendTs > this.SILENCE_FLUSH_MS) {
				this._send({ type: 'audio_stream_end' })
				this._lastMicSendTs = now
			}
		}

		const muteGain = this.audioContext.createGain()
		muteGain.gain.value = 0
		source.connect(processor)
		processor.connect(muteGain)
		muteGain.connect(this.audioContext.destination)
		this._micProcessor = processor
		this.onState('mic-started')
	}

	async stopMic() {
		if (this._micProcessor) {
			this._micProcessor.disconnect()
			this._micProcessor = null
		}
		if (this.micStream) {
			this.micStream.getTracks().forEach((t) => t.stop())
			this.micStream = null
		}
		if (this.analyser) {
			this.analyser.disconnect()
			this.analyser = null
		}
		if (this._audioLevelInterval) {
			cancelAnimationFrame(this._audioLevelInterval)
			this._audioLevelInterval = null
		}
		// Send final audio_stream_end
		this._send({ type: 'audio_stream_end' })
		this.onState('mic-stopped')
	}

	async startScreenCapture(displaySurface = 'monitor') {
		try {
			if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia)
				throw new Error('Condivisione schermo non supportata')
			this.screenStream = await navigator.mediaDevices.getDisplayMedia({
				video: {
					displaySurface: displaySurface,
					frameRate: 1, // Low FPS for bandwidth
				},
				audio: false,
			})

			// Start capturing and sending frames
			this._startScreenFrameCapture()
		} catch (error) {
			let message = 'Errore condivisione schermo sconosciuto'
			if (
				error.name === 'NotAllowedError' ||
				error.name === 'PermissionDeniedError'
			)
				message = 'Permesso condivisione schermo negato.'
			else if (error.message) message = error.message
			this.onError(message)
			throw error
		}
		this.onState('screen-started')
	}

	_startScreenFrameCapture() {
		if (!this.screenStream) return

		const video = document.createElement('video')
		video.srcObject = this.screenStream
		video.play()

		const canvas = document.createElement('canvas')
		const ctx = canvas.getContext('2d')

		// Capture and send frames at 1.5 FPS (like Python)
		const captureInterval = 1000 / 1.5 // ~667ms

		const captureFrame = () => {
			if (!this.screenStream || !this.screenStream.active) {
				return
			}

			try {
				// Set canvas size based on video
				if (video.videoWidth && video.videoHeight) {
					// Downscale for bandwidth (max width 1280 like Python)
					let width = video.videoWidth
					let height = video.videoHeight

					if (width > 1280) {
						height = Math.round(height * (1280 / width))
						width = 1280
					}

					canvas.width = width
					canvas.height = height

					// Draw video frame to canvas
					ctx.drawImage(video, 0, 0, width, height)

					// Convert to JPEG with quality 80 (like Python)
					canvas.toBlob(
						(blob) => {
							if (blob) {
								const reader = new FileReader()
								reader.onloadend = () => {
									const base64 = reader.result.split(',')[1]
									this._send({
										type: 'image',
										data: base64,
										mime: 'image/jpeg',
									})
								}
								reader.readAsDataURL(blob)
							}
						},
						'image/jpeg',
						0.8 // Quality 80%
					)
				}
			} catch (error) {
				console.error('Error capturing screen frame:', error)
			}

			// Schedule next capture
			this._screenCaptureTimeout = setTimeout(captureFrame, captureInterval)
		}

		// Start capturing after a short delay
		this._screenCaptureTimeout = setTimeout(captureFrame, 100)

		// Clean up when stream ends
		this.screenStream.getVideoTracks()[0].onended = () => {
			this.stopScreenCapture()
		}
	}

	async stopScreenCapture() {
		if (this._screenCaptureTimeout) {
			clearTimeout(this._screenCaptureTimeout)
			this._screenCaptureTimeout = null
		}
		if (this.screenStream) {
			this.screenStream.getTracks().forEach((t) => t.stop())
			this.screenStream = null
		}
		this.onState('screen-stopped')
	}

	async sendText(text) {
		this._send({ type: 'text', text })
	}

	async disconnect() {
		try {
			await this.stopMic()
		} catch (_) { }
		try {
			await this.stopScreenCapture()
		} catch (_) { }

		// Clear watchdog
		if (this._audioWatchdog) {
			clearInterval(this._audioWatchdog)
			this._audioWatchdog = null
		}

		// Destroy audio queue
		if (this.audioQueue) {
			this.audioQueue.destroy()
			this.audioQueue = null
		}

		if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.close()
		this._teardownAudio()
	}

	_teardownAudio() {
		if (this.audioContext) {
			try {
				this.audioContext.close()
			} catch (_) { }
			this.audioContext = null
		}
		this.micQueue = []
	}

	_triggerBargeIn() {
		try {
			// Clear ALL queued audio immediately (matches backend on interrupt)
			if (this.audioQueue) {
				this.audioQueue.clear()
			}

			this.isSpeaking = false
			// Guard mic for 150ms after barge-in to avoid immediate bounce-back
			this.playbackGuardUntil = Date.now() + 150

			// Instruct backend to end current turn now
			this._send({ type: 'force_turn' })
		} catch (e) {
			console.warn('Barge-in error:', e)
		}
	}

	_startAudioLevelMonitoring() {
		const updateLevel = () => {
			if (!this.analyser || !this.analyserDataArray) return

			this.analyser.getByteFrequencyData(this.analyserDataArray)

			// Calculate average audio level
			let sum = 0
			for (let i = 0; i < this.analyserDataArray.length; i++) {
				sum += this.analyserDataArray[i]
			}
			const average = sum / this.analyserDataArray.length

			// Emit audio level for visualization
			this.onAudioLevel(average)

			this._audioLevelInterval = requestAnimationFrame(updateLevel)
		}

		updateLevel()
	}
}
